import { GumletVideoPlayer } from '@/components/GumletVideoPlayer';
import { useSubscriptionContext } from '@/contexts/SubscriptionContext';
import { UpgradePrompt } from '@/components/upgrade/UpgradePrompt';
import { PlayCircle, Lock } from 'lucide-react';
import type { CourseLesson } from '@/types/courses';

interface PlayerVideoSectionProps {
  lesson: CourseLesson | null;
  hasPublishedLessons: boolean;
  onVideoEnded?: () => void;
}

export function PlayerVideoSection({
  lesson,
  hasPublishedLessons,
  onVideoEnded,
}: PlayerVideoSectionProps) {
  const { isActive, tierName, isLoading } = useSubscriptionContext();
  const isSubscribed = isActive && tierName !== 'Free';

  // Subscription gate — show upgrade prompt instead of video
  if (!isLoading && !isSubscribed && lesson?.video_id) {
    return (
      <div className="w-full rounded-xl overflow-hidden shadow-lg flex-shrink-0">
        <div className="bg-black aspect-video flex items-center justify-center rounded-xl relative">
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center p-6">
            <Lock className="h-12 w-12 text-gray-400 mb-4" />
            <UpgradePrompt
              feature="courses"
              variant="inline"
            />
          </div>
        </div>
      </div>
    );
  }

  // Has video — play it
  if (lesson?.video_id) {
    return (
      <div className="w-full rounded-xl overflow-hidden shadow-lg flex-shrink-0">
        <GumletVideoPlayer
          videoId={lesson.video_id}
          lessonId={lesson.id}
          onVideoEnded={onVideoEnded}
        />
      </div>
    );
  }

  // No video — placeholder
  return (
    <div className="bg-black aspect-video flex items-center justify-center rounded-xl">
      <div className="text-center text-gray-400">
        <PlayCircle className="h-16 w-16 mx-auto mb-2" />
        <p className="text-sm">
          {lesson
            ? 'Video coming soon'
            : hasPublishedLessons
              ? 'Select a lesson to begin'
              : 'No lessons available yet'}
        </p>
      </div>
    </div>
  );
}
